// EntityFormModal.jsx - Enhanced with multi-file support

import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { RefreshCw, Save, X, Upload, Trash2, XCircle, FileText, Image, FileSpreadsheet, Archive, File, CheckCircle2, AlertCircle, Mail, Activity, Bell, TrendingUp, Info, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useAlert } from "context/AlertContext";
import Draggable from "react-draggable";
import Select from "react-select";
import imageCompression from "browser-image-compression";
import { uploadSingleFile } from "services/upload-service/upload-service";
import axios from "axios";
import DateTimePicker from "modules/swiftboard/components/ui/DateTimePicker";

// Date-only fields store/consume a "YYYY-MM-DD" string (matches the old
// native <input type="date"> value shape that validation/buildPayload
// helpers across the app already expect) — these convert to/from the plain
// JS Date object DateTimePicker works with, parsed via local getters so the
// calendar never drifts a day off due to UTC parsing.
const parseDateOnly = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const formatDateOnly = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Memoizes the string→Date conversions so DateTimePicker only sees a new
// `value`/`minDate`/`maxDate` reference when the underlying string actually
// changes — otherwise every keystroke elsewhere in the form would re-allocate
// these Dates and re-trigger DateTimePicker's internal `value` effect,
// snapping its calendar view back mid-navigation.
const DateOnlyField = ({ value, min, max, onChange, ...props }) => {
  const dateValue = useMemo(() => parseDateOnly(value), [value]);
  const minDate = useMemo(() => parseDateOnly(min), [min]);
  const maxDate = useMemo(() => parseDateOnly(max), [max]);
  return (
    <DateTimePicker
      value={dateValue}
      minDate={minDate}
      maxDate={maxDate}
      showTime={false}
      onChange={(date) => onChange(formatDateOnly(date))}
      {...props}
    />
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────
// Safely read a (possibly nested) value from an object using a dot-path string.
// e.g. resolvePath(opt, "business_type.name") → opt.business_type?.name
const resolvePath = (obj, path) =>
  path?.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

// Build the visible label for an API-driven <select> option.
// Backward compatible: when only `optionLabel` is set it behaves exactly as before.
// New, opt-in props (any module can use them, old configs keep working):
//   • optionLabelFormatter(opt) → string  — full control over the label text
//   • optionSubLabel: "a.b.c"             — dot-path appended as " (value)"
const buildOptionLabel = (opt, field) => {
  if (typeof field.optionLabelFormatter === "function") {
    return field.optionLabelFormatter(opt);
  }

  const base = resolvePath(opt, field.optionLabel);

  if (field.optionSubLabel) {
    const sub = resolvePath(opt, field.optionSubLabel);
    if (sub !== undefined && sub !== null && sub !== "") {
      return `${base} (${sub})`;
    }
  }

  return base;
};

// ─── Multi-File Uploader ────────────────────────────────────────────────────
const MultiFileUploader = ({ field, value = [], onChange, hasError, error, projectTitle }) => {
  const { showAlert } = useAlert();

  const [files, setFiles] = useState([]); // internal list with status
  const [dragOver, setDragOver] = useState(false);
  const [cancelTokenMap, setCancelTokenMap] = useState({});
  const fileInputRef = useRef(null);

  // Hydrate from parent `value` ONLY when the local list is still empty
  // (initial mount / edit mode). After files exist locally, each successful
  // upload calls syncToParent → onChange → `value` changes → this effect would
  // re-run and replace `files` with just the completed ones, wiping out any
  // still-pending files mid "Upload all". Guarding on prev.length avoids that.
  useEffect(() => {
    if (!value?.length) return;
    setFiles((prev) => {
      if (prev.length > 0) return prev; // already managing files locally — don't clobber
      return value.map((f, index) => ({
        id: `existing-${index}`,
        name: f.file_name,
        size: f.file_size,
        file_url: f.file_url,
        status: "completed",
        progress: 100,
        type: "",
        file: null
      }));
    });
  }, [value]);

  // Sync completed files out to parent formData
  const syncToParent = useCallback((updatedFiles) => {
    const completed = updatedFiles
      .filter((f) => f.status === "completed")
      .map((f) => ({ file_name: f.name, file_url: f.file_url, file_size: f.size }));
    onChange(completed);
  }, [onChange]);

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
  };

  const getFileIcon = (name = "", type = "") => {
    const ext = name.split(".").pop().toLowerCase();
    if (type.includes("pdf") || ext === "pdf") return FileText;
    if (type.includes("image") || ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext)) return Image;
    if (type.includes("word") || ["doc", "docx"].includes(ext)) return FileText;
    if (type.includes("excel") || ["xls", "xlsx"].includes(ext)) return FileSpreadsheet;
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return Archive;
    return File;
  };

  const getFileColor = (name = "", type = "") => {
    const ext = name.split(".").pop().toLowerCase();
    if (type.includes("pdf") || ext === "pdf") return "#e05252";
    if (type.includes("image") || ["jpg", "jpeg", "png", "webp"].includes(ext)) return "#2a9d8f";
    if (type.includes("word") || ["doc", "docx"].includes(ext)) return "#3b82f6";
    if (type.includes("excel") || ["xls", "xlsx"].includes(ext)) return "#16a34a";
    if (["zip", "rar", "7z"].includes(ext)) return "#f59e0b";
    return "#6b7280";
  };

  const addFiles = (rawFiles) => {
    const accepted = rawFiles.filter((f) => {
      if (f.size > (field.maxSizeMB || 2048) * 1024 * 1024) return false;
      if (field.acceptedTypes?.length && !field.acceptedTypes.includes(f.type)) return false;
      return true;
    });

    const newEntries = accepted.map((f) => ({
      id: `${Date.now()}-${Math.random()}`,
      file: f,
      name: f.name,
      size: f.size,
      type: f.type,
      status: "pending",
      progress: 0,
      speed: null,
      file_url: null,
      error: null,
    }));

    setFiles((prev) => [...prev, ...newEntries]);
  };

  const removeFile = (id) => {
    const source = cancelTokenMap[id];
    if (source) source.cancel("Cancelled by user");
    setFiles((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      syncToParent(updated);
      return updated;
    });
  };

  const uploadFile = async (fileItem) => {

    if (!projectTitle?.trim()) {
      showAlert({
        type: "warning",
        message: "Please enter project title before uploading files."
      });

      return;
    }
    // Mark uploading
    setFiles((prev) =>
      prev.map((f) => f.id === fileItem.id ? { ...f, status: "uploading", progress: 0 } : f)
    );

    try {
      const source = axios.CancelToken.source();
      setCancelTokenMap((prev) => ({ ...prev, [fileItem.id]: source }));

      // Get presigned URL
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}file-uploader/generate-presigned-url`,
        {
          directory: process.env.REACT_APP_PROJECT_DIRECTORY || "project-files",
          projectTitle: projectTitle || "untitled-project",
          contentType: fileItem.type || "application/octet-stream",
        },
        { withCredentials: true }
      );
      const { url } = res.data.data;

      const startTime = Date.now();

      // Upload to S3
      await axios.put(url, fileItem.file, {
        headers: { "Content-Type": fileItem.type || "application/octet-stream" },
        cancelToken: source.token,
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          const elapsed = (Date.now() - startTime) / 1000;
          const speedMbps = elapsed > 0 ? ((e.loaded * 8) / (1024 * 1024) / elapsed).toFixed(1) : 0;
          setFiles((prev) =>
            prev.map((f) => f.id === fileItem.id ? { ...f, progress: pct, speed: speedMbps } : f)
          );
        },
      });

      const fileUrl = url.split("?")[0];

      setFiles((prev) => {
        const updated = prev.map((f) =>
          f.id === fileItem.id ? { ...f, status: "completed", progress: 100, file_url: fileUrl, speed: null } : f
        );
        syncToParent(updated);
        return updated;
      });

    } catch (err) {
      if (axios.isCancel(err)) {
        setFiles((prev) => prev.filter((f) => f.id !== fileItem.id));
      } else {
        setFiles((prev) =>
          prev.map((f) => f.id === fileItem.id ? { ...f, status: "failed", error: "Upload failed" } : f)
        );
      }
    } finally {
      setCancelTokenMap((prev) => {
        const next = { ...prev };
        delete next[fileItem.id];
        return next;
      });
    }
  };

  const uploadAll = async () => {
    const pending = files.filter((f) => f.status === "pending" || f.status === "failed");
    for (const f of pending) {
      await uploadFile(f);
    }
  };

  const retryFile = (fileItem) => uploadFile(fileItem);

  const pendingCount = files.filter((f) => f.status === "pending" || f.status === "failed").length;
  const uploadingCount = files.filter((f) => f.status === "uploading").length;
  const completedCount = files.filter((f) => f.status === "completed").length;

  return (
    <div className="col-span-2">
      <label className="block text-xs font-medium mb-1.5">
        {field.label}
        {field.required && <span className="text-danger ml-1">*</span>}
      </label>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(Array.from(e.dataTransfer.files)); }}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200
          ${dragOver
            ? "border-primary bg-primary/5 scale-[1.01]"
            : hasError
              ? "border-danger bg-danger_bg"
              : "border-border hover:border-primary/40 bg-surface_alt/40 hover:bg-primary/[0.03]"
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={field.acceptedTypes?.join(",")}
          onChange={(e) => { addFiles(Array.from(e.target.files)); e.target.value = ""; }}
        />

        <div className="flex flex-col items-center gap-3">
          {/* Icon badge */}
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-200 shadow-sm ${
            dragOver ? "bg-primary/20 scale-110 shadow-md" : "bg-primary/10"
          }`}>
            <Upload size={26} className={dragOver ? "text-primary" : "text-primary/70"} />
          </div>

          {/* Text */}
          <div className="space-y-1">
            <p className="text-sm font-semibold text-text_primary">
              {dragOver ? "Release to add files" : "Drag & drop your files here"}
            </p>
            <p className="text-[11px] text-text_muted">
              PDF, Word, Excel, ZIP, Images — max {field.maxSizeMB >= 1024 ? `${field.maxSizeMB / 1024} GB` : `${field.maxSizeMB} MB`} each
            </p>
          </div>

          {/* Explicit CTA button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary_hover active:scale-95 text-white text-xs font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-150"
          >
            <Upload size={13} />
            Choose Files
          </button>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {/* Stats + Upload-all bar */}
          <div className="flex items-center justify-between px-0.5">
            <span className="text-[11px] text-text_secondary">
              {files.length} file{files.length !== 1 ? "s" : ""} · {completedCount} ready
              {uploadingCount > 0 && ` · ${uploadingCount} uploading`}
            </span>
            {pendingCount > 0 && uploadingCount === 0 && (
              <button
                type="button"
                onClick={uploadAll}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold text-white bg-primary hover:bg-primary_hover active:scale-95 rounded-md shadow-sm transition-all duration-150"
              >
                <Upload size={11} />
                Upload all ({pendingCount})
              </button>
            )}
          </div>

          {/* File items */}
          {files.map((fileItem) => {
            const IconComp = getFileIcon(fileItem.name, fileItem.type);
            const color = getFileColor(fileItem.name, fileItem.type);
            const isUploading = fileItem.status === "uploading";
            const isCompleted = fileItem.status === "completed";
            const isFailed = fileItem.status === "failed";

            return (
              <div
                key={fileItem.id}
                className={`
                  relative rounded-lg border overflow-hidden transition-all duration-300
                  ${isCompleted ? "border-success_border bg-success_bg" : isFailed ? "border-danger_border bg-danger_bg" : "border-border bg-surface"}
                `}
              >
                {/* Progress fill background */}
                {isUploading && (
                  <div
                    className="absolute inset-0 bg-primary/5 transition-all duration-300 ease-out"
                    style={{ width: `${fileItem.progress}%` }}
                  />
                )}

                <div className="relative flex items-center gap-3 px-3 py-2.5">
                  {/* File type icon */}
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: `${color}18` }}
                  >
                    <IconComp size={15} style={{ color }} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-text_primary truncate">{fileItem.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-text_muted">{formatSize(fileItem.size)}</span>

                      {isUploading && (
                        <>
                          <span className="text-[11px] text-primary font-medium">{fileItem.progress}%</span>
                          {fileItem.speed && (
                            <span className="text-[11px] text-text_muted">{fileItem.speed} Mbps</span>
                          )}
                        </>
                      )}
                      {isCompleted && (
                        <span className="text-[11px] text-success font-medium flex items-center gap-1">
                          <CheckCircle2 size={10} />
                          Ready
                        </span>
                      )}
                      {isFailed && (
                        <span className="text-[11px] text-danger font-medium flex items-center gap-1">
                          <AlertCircle size={10} />
                          Failed
                        </span>
                      )}
                      {fileItem.status === "pending" && (
                        <button
                          type="button"
                          onClick={() => uploadFile(fileItem)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-white bg-primary hover:bg-primary_hover active:scale-95 rounded transition-all duration-150 shadow-sm"
                        >
                          <Upload size={9} />
                          Upload
                        </button>
                      )}
                      {isFailed && (
                        <button
                          type="button"
                          onClick={() => retryFile(fileItem)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-danger border border-danger/40 hover:bg-danger_bg active:scale-95 rounded transition-all duration-150"
                        >
                          <RefreshCw size={9} />
                          Retry
                        </button>
                      )}
                    </div>

                    {/* Progress bar */}
                    {isUploading && (
                      <div className="mt-1.5 h-1 bg-surface_alt rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${fileItem.progress}%` }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Remove / Cancel */}
                  <button
                    type="button"
                    onClick={() => removeFile(fileItem.id)}
                    disabled={isCompleted}
                    className={`flex-shrink-0 p-1 rounded transition-colors ${isCompleted
                      ? "text-text_muted/40 cursor-not-allowed"
                      : "text-text_muted hover:text-danger hover:bg-danger_bg"
                      }`}
                  >
                    {isUploading ? <XCircle size={14} /> : <Trash2 size={14} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasError && (
        <p className="text-xs text-danger mt-1.5">{error}</p>
      )}
    </div>
  );
};

// ─── Main Modal ─────────────────────────────────────────────────────────────
const NOTE_ICON_MAP = { mail: Mail, activity: Activity, bell: Bell, trending: TrendingUp, info: Info };
const NOTE_STYLE_MAP = {
  info:    { wrap: "bg-primary_tint border-primary/20",    iconWrap: "bg-primary/10",    icon: "text-primary",    title: "text-primary"    },
  tip:     { wrap: "bg-warning_bg/60 border-warning/20",   iconWrap: "bg-warning/10",    icon: "text-warning",    title: "text-warning"    },
  success: { wrap: "bg-success_bg/60 border-success/20",   iconWrap: "bg-success/10",    icon: "text-success",    title: "text-success"    },
};

const EntityFormModal = ({
  isOpen,
  onClose,
  id,
  title,
  fetchById,
  createFn,
  updateFn,
  dependenciesLoader,
  fields = [],
  validationSchema = {},
  onSuccess,
  contextNotes = [],
  creationGuide = null,
}) => {
  const { showAlert } = useAlert();
  const isEditMode = !!id;
  const nodeRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({});
  const [dependencies, setDependencies] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showGuide, setShowGuide] = useState(false);


  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  useEffect(() => {
    if (!isOpen) {
      setFormData({});
      setErrors({});
      setTouched({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      try {
        setLoadingData(true);
        const startTime = Date.now();

        let deps = {};
        if (dependenciesLoader) {
          deps = (await dependenciesLoader()) || {};
          setDependencies(deps);

          if (!isEditMode) {
            let initialData = {};
            fields.forEach(field => {
              if (field.type === "select" && field.is_enums_data && field.defaultOptionValue) {
                initialData[field.name] = field.defaultOptionValue;
              }
              if (field.type === "select" && field.is_api_data && field.isReadOnly && field.defaultOptionValue) {
                const options = deps[field.optionsKey] || [];
                const matched = options.find(opt => opt[field.optionLabel] === field.defaultOptionValue);
                if (matched) initialData[field.name] = matched.id;
              }
              if (field.type === "boolean") {
                initialData[field.name] = field.defaultValue !== undefined ? field.defaultValue : false;
              }
              if (field.isReadOnly && field.defaultValue) {
                initialData[field.name] = field.defaultValue;
              }
              // multi-file default
              if (field.type === "multi-file") {
                initialData[field.name] = [];
              }
              // multi-select default
              if (field.type === "select" && field.isMulti) {
                initialData[field.name] = [];
              }
            });
            setFormData(prev => ({ ...prev, ...initialData }));
          }
        }

        if (isEditMode && fetchById) {
          const res = await fetchById(id);
          // if (res?.data?.status) setFormData(res.data.data);
          if (res?.data?.status) {
            const data = res.data.data;

            const transformedData = {
              ...data,

              // Date conversion
              deadline: data.deadline
                ? new Date(data.deadline).toISOString().split("T")[0]
                : "",

              // Queued-until date → date-input format (kept only if queued)
              till_queuing_date: data.till_queuing_date
                ? new Date(data.till_queuing_date).toISOString().split("T")[0]
                : "",

              // Existing project files
              project_files: (data.original_projects?.[0]?.project_files || []).map(file => ({
                file_name: file.file_name,
                file_url: file.file_url,
                file_size: file.file_size
              }))
            };

            // Multi-select edit prefill: map a relation array on the fetched
            // record (e.g. `trade_of_works: [{id,...}]`) → an array of ids
            // for the form value (e.g. `trade_of_work_fids: [12,20,21]`).
            // Opt-in per field via `optionRelationKey` (+ optional
            // `optionRelationValueKey`, defaults to "id").
            fields.forEach(field => {
              if (field.type === "select" && field.isMulti && field.optionRelationKey) {
                const relation = data[field.optionRelationKey];
                if (Array.isArray(relation)) {
                  transformedData[field.name] = relation.map(
                    item => item[field.optionRelationValueKey || "id"]
                  );
                }
              }
              // Edit-mode value worked out from the fetched record, e.g. a flag that isn't stored
              if (typeof field.editValue === "function") {
                const edited = field.editValue(data, deps);
                if (edited !== undefined) transformedData[field.name] = edited;
              }
            });

            setFormData(transformedData);
          };
        }

        const elapsed = Date.now() - startTime;
        const remaining = 500 - elapsed;
        if (remaining > 0) await wait(remaining);

      } catch {
        showAlert({ type: "error", message: "Failed to load data" });
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [isOpen, id]);

  const validateField = (name, value) => {
    if (!validationSchema[name]) return "";
    return validationSchema[name](value, formData, dependencies);
  };

  const handleChange = (name, value) => {
    // A field's `onValueChange(value, formData, dependencies)` may return new values for
    // the fields it drives (e.g. picking a parent resets the order); those are re-validated too.
    const updates = fields.find(f => f.name === name)?.onValueChange?.(value, formData, dependencies) || {};
    const nextData = { ...formData, [name]: value, ...updates };
    setFormData(prev => ({ ...prev, [name]: value, ...updates }));

    const nextErrors = { [name]: validateField(name, value) };
    Object.keys(updates).forEach(key => {
      nextErrors[key] = validationSchema[key] ? validationSchema[key](updates[key], nextData, dependencies) : "";
    });
    setErrors(prev => ({ ...prev, ...nextErrors }));
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    let valid = true;

    fields.forEach(field => {
      // Skip validation for fields hidden by their `hidden(formData, dependencies)` condition
      if (typeof field.hidden === "function" && field.hidden(formData, dependencies)) return;

      const value = formData[field.name];
      let error = "";

      if (field.required) {
        if (field.type === "multi-file") {
          if (!Array.isArray(value) || value.length === 0) {
            error = `${field.label} is required — please upload at least one file`;
          }
        } else if (field.type === "select" && field.isMulti) {
          if (!Array.isArray(value) || value.length === 0) {
            error = `${field.label} is required — please select at least one option`;
          }
        } else if (value === undefined || value === null || value === "" || value === false) {
          error = `${field.label} is required`;
        }
      }

      if (!error && validationSchema[field.name]) {
        error = validationSchema[field.name](value, formData, dependencies);
      }

      if (error) {
        newErrors[field.name] = error;
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = {};
    fields.forEach(field => { if (field.required) allTouched[field.name] = true; });
    setTouched(prev => ({ ...prev, ...allTouched }));

    if (!validateForm()) return;

    try {
      setLoading(true);
      const startTime = Date.now();

      const response = isEditMode ? await updateFn(id, formData) : await createFn(formData);

      const elapsed = Date.now() - startTime;
      const remaining = 500 - elapsed;
      if (remaining > 0) await wait(remaining);

      if (response?.data?.status) {
        showAlert({ type: "success", message: `${title} ${isEditMode ? "updated" : "created"} successfully` });
        onSuccess?.();
        onClose();
      } else {
        showAlert({ type: "warning", message: response?.data?.message });
      }
    } catch (error) {
      showAlert({ type: "error", message: error?.response?.data?.message || "Operation failed" });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Form completion tracking — used for contextNotes thresholds
  const _requiredFields = fields.filter(f => f.required && !(typeof f.hidden === "function" && f.hidden(formData)));
  const _filledRequired = _requiredFields.filter(f => {
    const v = formData[f.name];
    if (f.type === "multi-file") return Array.isArray(v) && v.length > 0;
    if (f.type === "select" && f.isMulti) return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== null && v !== "" && v !== false;
  });
  const completionPct = _requiredFields.length > 0 ? _filledRequired.length / _requiredFields.length : 0;
  const visibleNotes = contextNotes.filter(n => {
    if (n.onlyCreate && isEditMode) return false;
    if (n.onlyEdit && !isEditMode) return false;
    return completionPct >= (n.threshold ?? 0);
  });
  const showNotesPanel = !isEditMode && contextNotes.length > 0;

  const getSelectStyles = (hasError) => ({
    control: (provided, state) => ({
      ...provided,
      minHeight: "34px",
      fontSize: "13px",
      borderWidth: "1px",
      backgroundColor: "var(--color-surface)",
      borderColor: hasError ? "var(--color-danger)" : state.isFocused ? "var(--color-border-strong)" : "var(--color-border)",
      boxShadow: state.isFocused ? (hasError ? "0 0 0 2px var(--color-danger-bg)" : "0 0 0 2px var(--color-border)") : "none",
      "&:hover": { borderColor: hasError ? "var(--color-danger)" : "var(--color-border-strong)" }
    }),
    menu: (provided) => ({ ...provided, fontSize: "13px", padding: "4px", backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "13px",
      marginBottom: "4px",
      borderRadius: "3px",
      padding: "6px 10px",
      backgroundColor: state.isSelected ? "var(--color-primary-tint)" : state.isFocused ? "var(--color-surface-alt)" : "transparent",
      color: "var(--color-text-primary)",
      cursor: "pointer"
    }),
    singleValue: (provided) => ({ ...provided, fontSize: "13px", color: "var(--color-text-primary)" }),
    placeholder: (provided) => ({ ...provided, fontSize: "13px", color: "var(--color-text-muted)" }),
    input: (provided) => ({ ...provided, color: "var(--color-text-primary)" })
  });

  // Portalled to <body> so the overlay is immune to an ancestor's layout — e.g.
  // a `space-y-*` container adding margin-top to this fixed overlay would shift
  // the backdrop down and leave a gap at the top of the screen.
  return createPortal(
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1999] p-4">
      <Draggable handle=".modal-header" cancel=".modal-header-actions" bounds="parent" nodeRef={nodeRef}>
        <div ref={nodeRef} className={`bg-surface text-text_primary w-full max-w-4xl rounded-lg shadow-lg text-sm transition-[filter] duration-300 ${showGuide && creationGuide && !isEditMode ? "blur-sm brightness-75 pointer-events-none select-none" : ""}`}>

          {/* Header */}
          <div className="modal-header cursor-move flex justify-between items-center px-5 py-4 border-b border-border bg-primary rounded-t-lg">
            <div>
              <h2 className="font-semibold text-base text-white">
                {isEditMode ? `Edit ${title}` : `Create ${title}`}
              </h2>
              <p className="text-xs text-white/70">
                {isEditMode ? "Update existing record" : "Fill in the details below to submit your project"}
              </p>
            </div>
            <div className="modal-header-actions flex items-center gap-2.5">
              {creationGuide && !isEditMode && (
                <button
                  type="button"
                  onClick={() => setShowGuide(v => !v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold border transition-all duration-200 ${
                    showGuide
                      ? "bg-white/20 border-white/30 text-white"
                      : "bg-white/10 border-white/15 text-white/65 hover:bg-white/18 hover:text-white"
                  }`}
                >
                  <HelpCircle size={12} />
                  How it works?
                  {showGuide ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
              )}
              <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
            {loadingData ? (
              <div className="flex flex-col items-center justify-center py-16">
                <RefreshCw className="w-5 h-5 animate-spin mb-2" />
                <p className="text-xs">Loading...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">

                  {fields.map(baseField => {
                    // Conditionally hide a field based on other form values
                    if (typeof baseField.hidden === "function" && baseField.hidden(formData, dependencies)) {
                      return null;
                    }

                    // `isReadOnly` may also be a (formData, dependencies) function, e.g. to lock a value another field decides
                    const field = typeof baseField.isReadOnly === "function"
                      ? { ...baseField, isReadOnly: baseField.isReadOnly(formData, dependencies) }
                      : baseField;

                    const value = formData[field.name] ?? "";
                    const hasError = (touched[field.name] || loading) && errors[field.name];

                    // ── MULTI-FILE ─────────────────────────────────────
                    if (field.type === "multi-file") {
                      return (
                        <MultiFileUploader
                          key={field.name}
                          field={field}
                          projectTitle={formData.title}
                          value={formData[field.name] || []}
                          onChange={(completedFiles) => handleChange(field.name, completedFiles)}
                          hasError={hasError}
                          error={errors[field.name]}
                        />
                      );
                    }

                    // ── SELECT ─────────────────────────────────────────
                    if (field.type === "select") {
                      let options = [];
                      if (field.is_enums_data && field.enums) {
                        options = field.enums.map(val => ({ value: val, label: val }));
                      } else if (field.is_api_data && field.optionsKey) {
                        options = (dependencies[field.optionsKey] || []).map(opt => ({
                          value: opt.id,
                          label: buildOptionLabel(opt, field)
                        }));
                      }
                      const selectValue = formData[field.name];

                      // ── MULTI-SELECT ────────────────────────────────
                      // Opt-in via `isMulti: true`. Value is stored as an
                      // array of option values, e.g. [1, 2, 3]. Backward
                      // compatible — single select stays untouched.
                      if (field.isMulti) {
                        const selectedValues = Array.isArray(selectValue) ? selectValue : [];
                        const selectedOptions = options.filter(o => selectedValues.includes(o.value));
                        return (
                          <div key={field.name} className={field.fullWidth ? "col-span-2" : ""}>
                            <label className="block text-xs font-medium mb-1">
                              {field.label}
                              {field.required && <span className="text-danger ml-1">*</span>}
                            </label>
                            <Select
                              isMulti
                              options={options}
                              value={selectedOptions}
                              onChange={(selected) => {
                                handleChange(field.name, selected ? selected.map(s => s.value) : []);
                                setTouched(prev => ({ ...prev, [field.name]: true }));
                              }}
                              onBlur={() => setTouched(prev => ({ ...prev, [field.name]: true }))}
                              placeholder={`Select ${field.label}`}
                              isSearchable={!field.isReadOnly}
                              isDisabled={field.isReadOnly}
                              closeMenuOnSelect={false}
                              styles={getSelectStyles(hasError)}
                            />
                            {hasError && <p className="text-xs text-danger mt-1">{errors[field.name]}</p>}
                          </div>
                        );
                      }

                      return (
                        <div key={field.name}>
                          <label className="block text-xs font-medium mb-1">
                            {field.label}
                            {field.required && <span className="text-danger ml-1">*</span>}
                          </label>
                          <Select
                            options={options}
                            value={options.find(o => o.value === selectValue) || null}
                            onChange={(selected) => {
                              handleChange(field.name, selected?.value || "");
                              setTouched(prev => ({ ...prev, [field.name]: true }));
                            }}
                            onBlur={() => setTouched(prev => ({ ...prev, [field.name]: true }))}
                            placeholder={`Select ${field.label}`}
                            isSearchable={!field.isReadOnly}
                            isDisabled={field.isReadOnly}
                            isClearable={!!field.isClearable && !field.isReadOnly}
                            styles={getSelectStyles(hasError)}
                          />
                          {hasError && <p className="text-xs text-danger mt-1">{errors[field.name]}</p>}
                        </div>
                      );
                    }

                    // ── TEXTAREA ───────────────────────────────────────
                    if (field.type === "textarea") {
                      return (
                        <div key={field.name} className={field.fullWidth ? "col-span-2" : ""}>
                          <label className="block text-xs font-medium mb-1">
                            {field.label}
                            {field.required && <span className="text-danger ml-1">*</span>}
                          </label>
                          <textarea
                            value={value}
                            readOnly={field.isReadOnly}
                            onChange={(e) => handleChange(field.name, e.target.value)}
                            onBlur={() => handleBlur(field.name)}
                            placeholder={`Enter ${field.label}`}
                            rows={field.rows || 4}
                            maxLength={field.maxLength}
                            className={`w-full px-3 py-1.5 text-sm rounded-md outline-none transition-all duration-200 border resize-none bg-transparent text-text_primary ${field.isReadOnly
                              ? "bg-surface_alt cursor-not-allowed text-text_muted"
                              : hasError
                                ? "border-danger focus:ring-2 focus:ring-danger/25"
                                : "border-border focus:border-border_strong focus:ring-2 focus:ring-border"
                              }`}
                          />
                          {field.maxLength && (
                            <p className="text-[11px] text-text_muted mt-0.5 text-right">
                              {(value || "").length}/{field.maxLength}
                            </p>
                          )}
                          {hasError && <p className="text-xs text-danger mt-1">{errors[field.name]}</p>}
                        </div>
                      );
                    }

                    // ── SINGLE FILE ────────────────────────────────────
                    if (field.type === "file") {
                      return (
                        <div key={field.name}>
                          <label className="block text-xs font-medium mb-1">
                            {field.label}
                            {field.required && <span className="text-danger ml-1">*</span>}
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              if (field.acceptedTypes?.length && !field.acceptedTypes.includes(file.type)) {
                                showAlert({ type: "error", message: "Invalid file type" });
                                return;
                              }
                              if (field.maxSizeMB && file.size > field.maxSizeMB * 1024 * 1024) {
                                showAlert({ type: "error", message: `File size must be under ${field.maxSizeMB} MB` });
                                return;
                              }
                              let processedFile = file;
                              if (field.convertSizeKB && file.type.startsWith("image/")) {
                                try {
                                  processedFile = await imageCompression(file, {
                                    maxSizeMB: field.convertSizeKB / 1024,
                                    maxWidthOrHeight: 1024,
                                    useWebWorker: true
                                  });
                                } catch {
                                  showAlert({ type: "error", message: "Image compression failed" });
                                  return;
                                }
                              }
                              setIsUploading(true);
                              setUploadProgress(0);
                              const result = await uploadSingleFile({
                                file: processedFile,
                                directory: field.uploadDirectory || "profile-images",
                                projectTitle: field.uploadProjectTitle || formData.first_name || "profile",
                                onProgress: setUploadProgress,
                                onSpeed: setUploadSpeed,
                              });
                              setIsUploading(false);
                              if (result.success) {
                                handleChange(field.name, result.file_url);
                              } else {
                                showAlert({ type: "error", message: "Upload failed" });
                              }
                            }}
                            className="w-full text-xs border border-border rounded-md p-1 bg-transparent text-text_primary"
                          />
                          {isUploading && (
                            <div className="mt-2 text-xs">
                              <div className="w-full bg-surface_alt rounded-full h-2">
                                <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                              </div>
                              <div className="flex justify-between mt-1">
                                <span>{uploadProgress}%</span>
                                <span>{uploadSpeed} Mbps</span>
                              </div>
                            </div>
                          )}
                          {formData[field.name] && (
                            <div className="mt-2">
                              <img src={formData[field.name]} alt="preview" className="h-16 w-16 object-cover rounded-md border border-border" />
                            </div>
                          )}
                          {touched[field.name] && errors[field.name] && (
                            <p className="text-xs text-danger mt-1">{errors[field.name]}</p>
                          )}
                        </div>
                      );
                    }

                    // ── BOOLEAN ────────────────────────────────────────
                    if (field.type === "boolean") {
                      const checked = !!formData[field.name];
                      const boolError = (touched[field.name] || loading) && errors[field.name];
                      const boolHint = typeof field.hint === "function" ? field.hint(formData, dependencies) : field.hint;
                      return (
                        <div key={field.name} className="flex flex-col justify-center">
                          <div className={field.wrapperClassName || "flex justify-between"}>
                            {field.uiType === "checkbox" ? (
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={field.isReadOnly}
                                onChange={(e) => {
                                  handleChange(field.name, e.target.checked);
                                  setTouched(prev => ({ ...prev, [field.name]: true }));
                                }}
                                className={`w-4 h-4 accent-primary cursor-pointer ${boolError ? "ring-2 ring-danger" : ""}`}
                              />
                            ) : (
                              <button
                                type="button"
                                disabled={field.isReadOnly}
                                onClick={() => {
                                  handleChange(field.name, !checked);
                                  setTouched(prev => ({ ...prev, [field.name]: true }));
                                }}
                                className={`relative w-11 h-6 rounded-full transition-all duration-300 ${boolError ? "ring-2 ring-danger" : ""} ${checked ? "bg-primary" : "bg-border_strong"} ${field.isReadOnly ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                              >
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-surface rounded-full shadow-md transform transition-all duration-300 ${checked ? "translate-x-5" : ""}`} />
                              </button>
                            )}
                            <label className="text-xs font-medium">
                              {field.label}
                              {field.required && <span className="text-danger ml-1">*</span>}
                            </label>
                          </div>
                          {boolError
                            ? <p className="text-xs text-danger mt-1">{errors[field.name]}</p>
                            : boolHint && <p className="text-[11px] text-text_muted mt-1">{boolHint}</p>}
                        </div>
                      );
                    }

                    // ── DATE ───────────────────────────────────────────
                    if (field.type === "date") {
                      return (
                        <div key={field.name} className={field.fullWidth ? "col-span-2" : ""}>
                          <label className="block text-xs font-medium mb-1">
                            {field.label}
                            {field.required && <span className="text-danger ml-1">*</span>}
                          </label>
                          <DateOnlyField
                            value={value}
                            min={field.min}
                            max={field.max}
                            onChange={(dateStr) => {
                              handleChange(field.name, dateStr);
                              setTouched(prev => ({ ...prev, [field.name]: true }));
                            }}
                            isDisabled={field.isReadOnly}
                            hasError={hasError}
                            placeholder={`Select ${field.label}`}
                          />
                          {hasError && <p className="text-xs text-danger mt-1">{errors[field.name]}</p>}
                        </div>
                      );
                    }

                    // ── CUSTOM ─────────────────────────────────────────
                    // Opt-in for inputs the types above don't cover (e.g. an editable list of rows):
                    // `render({ value, onChange, formData, dependencies, hasError, isReadOnly })` draws the control,
                    // the label, error and hint stay standard. `fullWidth` spans both columns.
                    if (field.type === "custom" && typeof field.render === "function") {
                      const customHint = typeof field.hint === "function" ? field.hint(formData, dependencies) : field.hint;
                      return (
                        <div key={field.name} className={field.fullWidth ? "md:col-span-2" : ""}>
                          {field.label && (
                            <label className="block text-xs font-medium mb-1">
                              {field.label}
                              {field.required && <span className="text-danger ml-1">*</span>}
                            </label>
                          )}
                          {field.render({
                            value: formData[field.name],
                            onChange: (next) => {
                              handleChange(field.name, next);
                              setTouched(prev => ({ ...prev, [field.name]: true }));
                            },
                            formData,
                            dependencies,
                            hasError: !!hasError,
                            isReadOnly: !!field.isReadOnly,
                          })}
                          {hasError
                            ? <p className="text-xs text-danger mt-1">{errors[field.name]}</p>
                            : customHint && <p className="text-[11px] text-text_muted mt-1">{customHint}</p>}
                        </div>
                      );
                    }

                    // ── DEFAULT INPUT ──────────────────────────────────
                    // Optional helper line under the input: a string or a (formData, dependencies) function
                    const hint = typeof field.hint === "function" ? field.hint(formData, dependencies) : field.hint;
                    return (
                      <div key={field.name}>
                        <label className="block text-xs font-medium mb-1">
                          {field.label}
                          {field.required && <span className="text-danger ml-1">*</span>}
                        </label>
                        <input
                          type={field.type || "text"}
                          value={value}
                          readOnly={field.isReadOnly}
                          onChange={(e) => handleChange(field.name, e.target.value)}
                          placeholder={`Enter ${field.label}`}
                          onBlur={() => handleBlur(field.name)}
                          min={field.min}
                          max={field.max}
                          maxLength={field.maxLength}
                          className={`w-full px-3 py-1.5 text-sm rounded-md outline-none transition-all duration-200 border bg-transparent text-text_primary ${field.isReadOnly
                            ? "bg-surface_alt cursor-not-allowed text-text_muted"
                            : hasError
                              ? "border-danger focus:ring-2 focus:ring-danger/25"
                              : "border-border focus:border-border_strong focus:ring-2 focus:ring-border"
                            }`}
                        />
                        {hasError
                          ? <p className="text-xs text-danger mt-1">{errors[field.name]}</p>
                          : hint && <p className="text-[11px] text-text_muted mt-1">{hint}</p>}
                      </div>
                    );
                  })}

                </div>

                {/* ── Context Notes + Progress Panel ── */}
                {showNotesPanel && (
                  <div className="px-5 pb-4 space-y-2.5">
                    {/* Completion progress bar */}
                    <div className="bg-surface_alt/60 border border-border rounded-lg px-4 py-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text_muted">Form Completion</span>
                        <span className={`text-[11px] font-bold tabular-nums transition-colors duration-300 ${completionPct >= 1 ? "text-success" : completionPct >= 0.8 ? "text-primary" : "text-text_muted"}`}>
                          {Math.round(completionPct * 100)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-border rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500 ease-out"
                          style={{
                            width: `${completionPct * 100}%`,
                            background: completionPct >= 1
                              ? "var(--color-success)"
                              : completionPct >= 0.8
                                ? "var(--color-primary)"
                                : "var(--color-warning)",
                          }}
                        />
                      </div>
                      {completionPct >= 1 && (
                        <p className="text-[10.5px] text-success mt-1.5 flex items-center gap-1 font-medium">
                          <CheckCircle2 size={10} /> All required fields filled — ready to submit
                        </p>
                      )}
                    </div>

                    {/* Contextual note cards */}
                    {visibleNotes.map(note => {
                      const style = NOTE_STYLE_MAP[note.type] || NOTE_STYLE_MAP.info;
                      const IconComp = NOTE_ICON_MAP[note.icon] || Info;
                      return (
                        <div
                          key={note.id}
                          className={`flex items-start gap-3 px-3.5 py-3 rounded-lg border transition-all duration-300 ${style.wrap}`}
                        >
                          <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${style.iconWrap}`}>
                            <IconComp size={13} className={style.icon} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-[11.5px] font-semibold mb-0.5 ${style.title}`}>{note.title}</p>
                            <p className="text-[11px] text-text_secondary leading-relaxed">{note.message}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-3 px-5 py-3 border-t border-border bg-surface_alt rounded-b-lg">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs border border-border rounded-md text-text_secondary hover:bg-background transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-3 py-1.5 text-xs text-white bg-primary hover:bg-primary_hover rounded-md flex items-center gap-1 transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" />
                        {isEditMode ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>
                        <Save size={14} />
                        {isEditMode ? "Update" : "Create"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Draggable>
    </div>

    {/* ── Nested "How it works" Guide Dialog — own fixed overlay above z-[1999] ── */}
    {showGuide && creationGuide && !isEditMode && (
      <div
        className="fixed inset-0 flex items-center justify-center z-[2000] bg-black/30 backdrop-blur-[1px]"
        onClick={() => setShowGuide(false)}
      >
          <div
            className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Guide Header */}
            <div className="bg-primary px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-white/55 text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full mb-3">
                    <HelpCircle size={9} /> Project Guide
                  </div>
                  <h3 className="text-[17px] font-semibold text-white leading-tight">How does this work?</h3>
                  <p className="text-[12px] text-white/45 mt-1">Everything you need to know — 4 simple steps</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowGuide(false)}
                  className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-all mt-1"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Steps */}
            <div className="px-6 py-5">
              {(() => {
                const STEP_COLORS = [
                  { bg: "bg-primary_tint",   icon: "text-primary",   num: "bg-primary"   },
                  { bg: "bg-warning_bg",     icon: "text-warning",   num: "bg-warning"   },
                  { bg: "bg-success_bg",     icon: "text-success",   num: "bg-success"   },
                  { bg: "bg-secondary_tint", icon: "text-secondary", num: "bg-secondary" },
                ];
                return creationGuide.steps.map((step, i) => {
                  const IconComp = NOTE_ICON_MAP[step.icon] || Info;
                  const isLast = i === creationGuide.steps.length - 1;
                  const color = STEP_COLORS[i] || STEP_COLORS[0];
                  return (
                    <div key={i} className={`flex items-start gap-4 relative ${!isLast ? "pb-6" : ""}`}>
                      {/* vertical connector */}
                      {!isLast && (
                        <div className="absolute left-[21px] top-11 bottom-0 w-px bg-border z-0" />
                      )}
                      {/* icon */}
                      <div className={`relative flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center z-10 ${color.bg}`}>
                        <IconComp size={18} className={color.icon} />
                        <span className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full ${color.num} text-white text-[9px] font-bold flex items-center justify-center shadow-sm`}>
                          {i + 1}
                        </span>
                      </div>
                      {/* content */}
                      <div className="flex-1 pt-1.5">
                        <p className="text-[13.5px] font-semibold text-text_primary mb-0.5">{step.title}</p>
                        <p className="text-[12px] text-text_secondary leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border bg-surface_alt flex items-center justify-between gap-4">
              <p className="text-[11.5px] text-text_muted leading-snug">The form is waiting — fill it at your own pace.</p>
              <button
                type="button"
                onClick={() => setShowGuide(false)}
                className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary_hover text-white text-[12.5px] font-semibold rounded-lg transition-colors shadow-sm"
              >
                Got it, let's start
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default EntityFormModal;